import { format, parseISO } from 'date-fns'

// ── Frontend types ──────────────────────────────────────────────────────

export interface MultilocRow {
  id: string
  backendId: string | null
  storeGroupId: string
  presentationLocation: string
  numberOfPresentations: string
  numberOfFacings: string
  estLift: string
  setDate: Date | null
  exitDate: Date | null
}

export function createEmptyRow(): MultilocRow {
  return {
    id: crypto.randomUUID(),
    backendId: null,
    storeGroupId: '',
    presentationLocation: '',
    numberOfPresentations: '',
    numberOfFacings: '',
    estLift: '',
    setDate: null,
    exitDate: null,
  }
}

// ── API types (matching OpenAPI spec) ───────────────────────────────────

export interface MultilocRead {
  id?: string | null
  store_group_id: string
  presentation_location: string
  number_of_presentations: number
  number_of_facings: number
  estimated_lift_percent: number
  set_date: string | null
  exit_date: string | null
}

export interface MultilocCreate {
  store_group_id: string
  presentation_location: string
  number_of_presentations: number
  number_of_facings: number
  estimated_lift_percent: number
  set_date: string | null
  exit_date: string | null
}

export interface MultilocUpdate {
  id?: string | null
  store_group_id: string
  presentation_location: string
  number_of_presentations: number
  number_of_facings: number
  estimated_lift_percent: number
  set_date: string | null
  exit_date: string | null
}

export interface MultilocFullResponse {
  items: MultilocRead[]
}

export interface MultilocLineItem {
  id: string
  sku_number: string | null
  description: string | null
}

export interface MultilocLinesResponse {
  data: MultilocLineItem[]
  pagination: {
    skip: number
    limit: number
    total: number
    has_more: boolean
  }
}

export interface CreateMultilocRequest {
  items: MultilocCreate[]
  source_plan_id?: string | null
  source_line_id?: string | null
}

export interface UpdateMultilocRequest {
  items: MultilocUpdate[]
}

// ── Mapping helpers ─────────────────────────────────────────────────────

function dateToString(date: Date | null): string | null {
  return date ? format(date, 'yyyy-MM-dd') : null
}

function stringToDate(str: string | null): Date | null {
  return str ? parseISO(str) : null
}

export const DEFAULT_GROUP_SENTINEL = '__default__'
export const ALL_GROUPS_SENTINEL = '__all_groups__'

/** Convert API response → frontend rows */
export function mapApiToRows(items: MultilocRead[]): MultilocRow[] {
  return items.map(item => ({
    id: crypto.randomUUID(),
    backendId: item.id ?? null,
    storeGroupId: item.store_group_id || DEFAULT_GROUP_SENTINEL,
    presentationLocation: item.presentation_location,
    numberOfPresentations: String(item.number_of_presentations),
    numberOfFacings: String(item.number_of_facings),
    estLift: String(item.estimated_lift_percent),
    setDate: stringToDate(item.set_date),
    exitDate: stringToDate(item.exit_date),
  }))
}

/** Convert frontend rows → CreateMultilocRequest */
export function mapRowsToCreateRequest(
  rows: MultilocRow[]
): CreateMultilocRequest {
  return {
    items: rows.map(row => ({
      store_group_id:
        row.storeGroupId === DEFAULT_GROUP_SENTINEL ? '' : row.storeGroupId,
      presentation_location: row.presentationLocation,
      number_of_presentations: Number(row.numberOfPresentations) || 0,
      number_of_facings: Number(row.numberOfFacings) || 0,
      estimated_lift_percent: Number(row.estLift) || 0,
      set_date: dateToString(row.setDate),
      exit_date: dateToString(row.exitDate),
    })),
  }
}

/** Convert frontend rows → UpdateMultilocRequest */
export function mapRowsToUpdateRequest(
  rows: MultilocRow[]
): UpdateMultilocRequest {
  return {
    items: rows.map(row => ({
      id: row.backendId,
      store_group_id:
        row.storeGroupId === DEFAULT_GROUP_SENTINEL ? '' : row.storeGroupId,
      presentation_location: row.presentationLocation,
      number_of_presentations: Number(row.numberOfPresentations) || 0,
      number_of_facings: Number(row.numberOfFacings) || 0,
      estimated_lift_percent: Number(row.estLift) || 0,
      set_date: dateToString(row.setDate),
      exit_date: dateToString(row.exitDate),
    })),
  }
}
